import React from "react";
import {
    Box,
    Typography,
    TextField,
    FormControlLabel,
    MenuItem,
} from "@mui/material";
import SquareCheckbox from "../filters/squareCheckbox";

function FigmaTextInput({
                            label,
                            value,
                            onChange,
                            name,
                            error,
                            helperText,
                            ...props
                        }) {
    return (
        <Box sx={{ position: "relative", width: "100%", height: 59 }}>
            <TextField
                {...props}
                name={name}
                value={value}
                onChange={onChange}
                error={error}
                helperText={helperText}
                focused={true}
                variant="outlined"
                size="small"
                fullWidth
                label={label}
                InputLabelProps={{
                    sx: {
                        fontFamily: "'Zen Maru Gothic', sans-serif",
                        fontSize: 16,
                        color: "#111111",
                        "&.Mui-focused": {
                            color: "#111111",
                        },
                    },
                }}
                InputProps={{
                    sx: {
                        fontFamily: "'Zen Maru Gothic', sans-serif",
                        fontSize: 16,
                        "& .MuiInputBase-input": {
                            py: 1.1,
                            pl: 1,
                        },
                        "& fieldset": {
                            borderColor: "#111111",
                            borderWidth: 1,
                            borderRadius: "4px",
                        },
                        "&:hover fieldset": {
                            borderColor: "#111111",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#111111",
                        },
                    },
                }}
            />
        </Box>
    );
}

function FigmaSelectInput({
                              label,
                              value,
                              onChange,
                              options,
                              name,
                              error,
                              helperText,
                          }) {
    return (
        <Box sx={{ position: "relative", width: "100%", height: 59 }}>
            <TextField
                select
                name={name}
                value={value}
                onChange={onChange}
                error={error}
                helperText={helperText}
                focused={true}
                variant="outlined"
                size="small"
                fullWidth
                label={label}
                InputLabelProps={{
                    sx: {
                        fontFamily: "'Zen Maru Gothic', sans-serif",
                        fontSize: 16,
                        color: "#111111",
                        "&.Mui-focused": {
                            color: "#111111",
                        },
                    },
                }}
                InputProps={{
                    sx: {
                        fontFamily: "'Zen Maru Gothic', sans-serif",
                        fontSize: 16,
                        "& .MuiInputBase-input": {
                            py: 1.1,
                            pl: 1,
                        },
                        "& fieldset": {
                            borderColor: "#111111",
                            borderWidth: 1,
                            borderRadius: "4px",
                        },
                        "&:hover fieldset": {
                            borderColor: "#111111",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#111111",
                        },
                    },
                }}
            >
                {options.map((opt) => (
                    <MenuItem key={opt.id}  value={String(opt.id)}>
                        {opt.Description}
                    </MenuItem>
                ))}
            </TextField>
        </Box>
    );
}

export default function DeliveryDetailsSection({
                                                   t,
                                                   formik,
                                                   addressOptions = [],
                                                   countryOptions = [],
                                               }) {
    const isNewAddress = formik.values.addNewAddress;

    const fieldError = (name) =>
        Boolean(formik.touched[name] && formik.errors[name]);

    const fieldHelperText = (name) =>
        formik.touched[name] && formik.errors[name]
            ? formik.errors[name]
            : "";

    const handleDeliveryAddressChange = (event) => {
        const id = event.target.value;
        formik.setFieldValue("addressId", id);

        const addr = addressOptions.find((a) => String(a.id) === String(id));

        if (addr) {
            formik.setFieldValue("deliveryName", addr.name || "");
            formik.setFieldValue("fullName", "");
            formik.setFieldValue("street", addr.Address || "");
            formik.setFieldValue("address2", "");
            formik.setFieldValue("postalCode", addr.ZipCode || "");
            formik.setFieldValue("city", addr.City || "");
            formik.setFieldValue("addressLabel", "");
            formik.setFieldValue("company", "");
            formik.setFieldValue("phone", addr.AddrPhone || "");
            formik.setFieldValue("email", addr.email || "");
            formik.setFieldValue("country", addr.CountryId || "");
        }
    };

    const handleAddNewChange = (event) => {
        const checked = event.target.checked;
        formik.setFieldValue("addNewAddress", checked);

        if (checked) {
            formik.setFieldValue("addressId", "");
            formik.setFieldValue("deliveryName", "");
            formik.setFieldValue("fullName", "");
            formik.setFieldValue("street", "");
            formik.setFieldValue("address2", "");
            formik.setFieldValue("postalCode", "");
            formik.setFieldValue("city", "");
            formik.setFieldValue("addressLabel", "");
            formik.setFieldValue("company", "");
            formik.setFieldValue("phone", "");
            formik.setFieldValue("email", "");
            formik.setFieldValue("country", "");
        } else {
            const first = addressOptions[0];
            formik.setFieldValue("addressId", first?.id || "");
            formik.setFieldValue("deliveryName", first?.name || "");
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: "30px",
                maxWidth: 601,
                width: "100%",
            }}
        >
            <Typography
                sx={{
                    fontFamily: "'Zen Maru Gothic', sans-serif",
                    fontWeight: 900,
                    fontSize: 17,
                    lineHeight: "110%",
                    textTransform: "uppercase",
                    color: "#111111",
                }}
            >
                {t("Delivery information")}
            </Typography>

            <Box sx={{ width: "100%" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: "flex-start",
                        gap: { xs: 2.5, md: "29px" },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                            width: { xs: "100%", md: 286 },
                            flexShrink: 0,
                        }}
                    >
                        {isNewAddress ? (
                            <FigmaTextInput
                                label={t("Delivery address")}
                                name="deliveryName"
                                value={formik.values.deliveryName}
                                onChange={formik.handleChange}
                                error={fieldError("deliveryName")}
                                helperText={fieldHelperText("deliveryName")}
                            />
                        ) : (
                            <FigmaSelectInput
                                label={t("Delivery address")}
                                name="addressId"
                                value={formik.values.addressId}
                                onChange={handleDeliveryAddressChange}
                                options={addressOptions}
                                error={fieldError("addressId")}
                                helperText={fieldHelperText("addressId")}
                            />
                        )}

                        <FigmaTextInput
                            label={t("Name")}
                            name="fullName"
                            value={formik.values.fullName}
                            onChange={formik.handleChange}
                            error={fieldError("fullName")}
                            helperText={fieldHelperText("fullName")}
                        />
                        <FigmaTextInput
                            label={t("Street")}
                            name="street"
                            value={formik.values.street}
                            onChange={formik.handleChange}
                            error={fieldError("street")}
                            helperText={fieldHelperText("street")}
                        />
                        <FigmaTextInput
                            label={t("Address 2")}
                            name="address2"
                            value={formik.values.address2}
                            onChange={formik.handleChange}
                            error={fieldError("address2")}
                            helperText={fieldHelperText("address2")}
                        />
                        <FigmaTextInput
                            label={t("Zip Code")}
                            name="postalCode"
                            value={formik.values.postalCode}
                            onChange={formik.handleChange}
                            error={fieldError("postalCode")}
                            helperText={fieldHelperText("postalCode")}
                        />
                        <FigmaTextInput
                            label={t("City")}
                            name="city"
                            value={formik.values.city}
                            onChange={formik.handleChange}
                            error={fieldError("city")}
                            helperText={fieldHelperText("city")}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                            width: { xs: "100%", md: 286 },
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                height: 20,
                                mb: 5,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <SquareCheckbox
                                        checked={isNewAddress}
                                        onChange={handleAddNewChange}
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            p: 0,
                                            mr: 1,
                                            "& .MuiSvgIcon-root": {
                                                fontSize: 20,
                                            },
                                        }}
                                    />
                                }
                                label={t("Add new address")}
                                sx={{
                                    m: 0,
                                    "& .MuiTypography-root": {
                                        fontFamily: "'Roboto Condensed', sans-serif",
                                        fontSize: 16,
                                        color: "#111111",
                                    },
                                }}
                            />
                        </Box>

                        <FigmaTextInput
                            label={t("Address type")}
                            name="addressLabel"
                            value={formik.values.addressLabel}
                            onChange={formik.handleChange}
                            error={fieldError("addressLabel")}
                            helperText={fieldHelperText("addressLabel")}
                        />
                        <FigmaTextInput
                            label={t("Company name")}
                            name="company"
                            value={formik.values.company}
                            onChange={formik.handleChange}
                            error={fieldError("company")}
                            helperText={fieldHelperText("company")}
                        />
                        <FigmaTextInput
                            label={t("Phone")}
                            name="phone"
                            value={formik.values.phone}
                            onChange={formik.handleChange}
                            error={fieldError("phone")}
                            helperText={fieldHelperText("phone")}
                        />
                        <FigmaTextInput
                            label={t("E-mail")}
                            name="email"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            error={fieldError("email")}
                            helperText={fieldHelperText("email")}
                        />
                        <FigmaSelectInput
                            label={t("Country")}
                            name="country"
                            value={formik.values.country}
                            onChange={formik.handleChange}
                            options={countryOptions}
                            error={fieldError("country")}
                            helperText={fieldHelperText("country")}
                        />
                    </Box>
                </Box>

                <Box sx={{ mt: 3, position: "relative", width: "100%" }}>
                    <Box
                        sx={{
                            position: "absolute",
                            left: 10,
                            top: 0,
                            px: "10px",
                            pt: "5px",
                            pb: "5px",
                            bgcolor: "#F0F0F0",
                            borderRadius: "4px",
                            zIndex: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: "'Zen Maru Gothic', sans-serif",
                                fontSize: 16,
                                lineHeight: "110%",
                                color: "#8D8D8D",
                            }}
                        >
                            {t("Additional information")}
                        </Typography>
                    </Box>

                    <TextField
                        multiline
                        minRows={3}
                        name="comment"
                        value={formik.values.comment}
                        onChange={formik.handleChange}
                        placeholder={t("Notes for the order,the address,etc")}
                        fullWidth
                        variant="outlined"
                        sx={{
                            mt: "14px",
                            "& .MuiInputBase-root": {
                                borderRadius: "5px",
                                borderColor: "#111111",
                            },
                            "& fieldset": {
                                borderColor: "#111111",
                                borderWidth: 1,
                                borderRadius: "5px",
                            },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
}